import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { AlertCircle, CheckCircle, Lock } from "lucide-react";
import axios from "axios";
import { formatApiErrorDetail, useAuth } from "@/App";

const L = {
  bg: { title: "Нова парола", desc: "Въведете новата си парола.", pass: "Нова парола", confirm: "Потвърдете паролата", reset: "Запази парола", resetting: "Записване...", success: "Паролата е променена успешно!", login: "Вход", mismatch: "Паролите не съвпадат", short: "Паролата трябва да е поне 6 символа" },
  en: { title: "Reset Password", desc: "Enter your new password.", pass: "New Password", confirm: "Confirm Password", reset: "Save Password", resetting: "Saving...", success: "Password has been reset successfully!", login: "Sign in", mismatch: "Passwords do not match", short: "Password must be at least 6 characters" },
  es: { title: "Nueva contrasena", desc: "Ingresa tu nueva contrasena.", pass: "Nueva contrasena", confirm: "Confirmar contrasena", reset: "Guardar contrasena", resetting: "Guardando...", success: "La contrasena se ha restablecido con exito!", login: "Iniciar sesion", mismatch: "Las contrasenas no coinciden", short: "La contrasena debe tener al menos 6 caracteres" },
  de: { title: "Neues Passwort", desc: "Geben Sie Ihr neues Passwort ein.", pass: "Neues Passwort", confirm: "Passwort bestatigen", reset: "Passwort speichern", resetting: "Speichern...", success: "Passwort erfolgreich zuruckgesetzt!", login: "Anmelden", mismatch: "Passworter stimmen nicht uberein", short: "Passwort muss mindestens 6 Zeichen lang sein" },
  ar: { title: "كلمة مرور جديدة", desc: "ادخل كلمة المرور الجديدة.", pass: "كلمة المرور الجديدة", confirm: "تاكيد كلمة المرور", reset: "حفظ كلمة المرور", resetting: "جاري الحفظ...", success: "تم اعادة تعيين كلمة المرور بنجاح!", login: "تسجيل الدخول", mismatch: "كلمات المرور غير متطابقة", short: "يجب ان تكون كلمة المرور 6 احرف على الاقل" },
  zh: { title: "重置密码", desc: "输入您的新密码。", pass: "新密码", confirm: "确认密码", reset: "保存密码", resetting: "保存中...", success: "密码重置成功！", login: "登录", mismatch: "两次密码不一致", short: "密码至少6个字符" },
};

export default function ResetPassword() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const l = L[language] || L.en;
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirmPassword) {
      setError(l.mismatch);
      return;
    }
    if (password.length < 6) {
      setError(l.short);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F0F9FF] via-[#FAFBFC] to-[#F0FDF4] flex flex-col">
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-6 sm:mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-3 bg-[#6366F1]/10">
              <Lock className="w-8 h-8 text-[#6366F1]" />
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-[#1A1A2E] mb-2">{l.title}</h1>
            <p className="text-sm text-[#94A3B8] max-w-sm mx-auto">{l.desc}</p>
          </div>

          <Card className="card-elevated border-0">
            <CardContent className="p-6">
              {success ? (
                <div className="text-center space-y-4">
                  <div className="flex items-center justify-center">
                    <CheckCircle className="w-12 h-12 text-[#10B981]" />
                  </div>
                  <p className="text-sm text-[#64748B]">{l.success}</p>
                  <Link to="/login" className="block">
                    <Button className="w-full h-11 gradient-primary text-white rounded-xl" data-testid="goto-login-btn">
                      {l.login}
                    </Button>
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <div className="flex items-center space-x-2 p-3 bg-red-50 text-red-600 rounded-lg text-sm" data-testid="reset-error">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" /><span>{error}</span>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-[#1A1A2E] text-sm font-medium">{l.pass}</Label>
                    <Input id="password" type="password" placeholder="••••••••" value={password}
                      onChange={(e) => setPassword(e.target.value)} required data-testid="reset-password-input"
                      className="h-11 bg-[#F8FAFC] border-[#E2E8F0] focus:ring-[#0EA5E9] focus:border-[#0EA5E9] rounded-xl" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm" className="text-[#1A1A2E] text-sm font-medium">{l.confirm}</Label>
                    <Input id="confirm" type="password" placeholder="••••••••" value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)} required data-testid="reset-confirm-input"
                      className="h-11 bg-[#F8FAFC] border-[#E2E8F0] focus:ring-[#0EA5E9] focus:border-[#0EA5E9] rounded-xl" />
                  </div>
                  <Button type="submit" disabled={loading} data-testid="reset-submit-btn"
                    className="w-full h-11 gradient-primary hover:opacity-90 text-white font-medium rounded-xl shadow-lg shadow-sky-500/20">
                    {loading ? l.resetting : l.reset}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
